import { ReactNode, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';

import { usePerpetualMarketsStoreView } from '#/features/perpetuals/market-info/markets-context';

export interface MarketsRoutingView {
  readonly status: 'loading' | 'error' | 'done';
  readonly error: string;
  readonly symbols: string[];
  readonly defaultSymbol: string | null;
  readonly hasSymbol: (symbol: string) => boolean;
}

export const [useFutureMarketsRoutingView, RoutingViewProvider] =
  createSafeContext<MarketsRoutingView>('MarketsRoutingView');

interface Props {
  readonly children: ReactNode;
  readonly lastVisitedSymbol: string | null;
}

export function FutureMarketsRoutingProvider(props: Props) {
  const { lastVisitedSymbol } = props;
  const marketsStoreView = usePerpetualMarketsStoreView();

  const view = useMemo(
    () => createView(marketsStoreView, lastVisitedSymbol),
    [lastVisitedSymbol, marketsStoreView],
  );

  return (
    <RoutingViewProvider value={view}>{props.children}</RoutingViewProvider>
  );
}

function createView(
  storeView: ReturnType<typeof usePerpetualMarketsStoreView>,
  lastVisitedSymbol: string | null,
): MarketsRoutingView {
  const symbols = symbolsView(storeView);
  const defaultSymbol = deriveDefaultSymbol(storeView, lastVisitedSymbol);
  const hasSymbol = (symbol: string | undefined) => {
    if (symbol == null) return false;
    return storeView.getMarket(symbol) != null;
  };

  return {
    status: storeView.status,
    error: storeView.error,
    symbols,
    defaultSymbol,
    hasSymbol,
  };
}

function symbolsView(
  storeView: ReturnType<typeof usePerpetualMarketsStoreView>,
): MarketsRoutingView['symbols'] {
  return storeView.getAllMarkets().map((market) => market.symbol);
}

function deriveDefaultSymbol(
  stateView: ReturnType<typeof usePerpetualMarketsStoreView>,
  lastVisitedSymbol: string | null,
): MarketsRoutingView['defaultSymbol'] {
  const lastVisitedMarket =
    lastVisitedSymbol != null ? stateView.getMarket(lastVisitedSymbol) : null;

  const defaultMarket =
    lastVisitedMarket ??
    stateView.getMarket('ETH-USD-PERP') ??
    stateView.getMarket('BTC-USD-PERP') ??
    null;

  return defaultMarket?.symbol ?? null;
}
